import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { clientsAPI } from '@shared/apiClient';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in webpack/vite
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Geocode results by client id for this session (null = attempted but failed),
// so re-renders don't re-request addresses the backend couldn't geocode.
const geocodeResultCache = new Map();

const ClientsMap = ({ clients, onClientClick, draggedClient, onDragStart, onDragEnd }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [isGeocoding, setIsGeocoding] = useState(false);

  useEffect(() => {
    // Initialize map
    if (!mapInstanceRef.current && mapRef.current) {
      mapInstanceRef.current = L.map(mapRef.current).setView([29.7604, -95.3698], 10); // Default to Houston area

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(mapInstanceRef.current);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapInstanceRef.current || !clients || clients.length === 0) {
      // Clear markers if no clients
      markersRef.current.forEach(marker => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.removeLayer(marker);
        }
      });
      markersRef.current = [];
      return;
    }

    // Clear existing markers
    markersRef.current.forEach(marker => {
      mapInstanceRef.current.removeLayer(marker);
    });
    markersRef.current = [];

    // Geocode addresses and add markers with rate limiting
    const geocodeAndAddMarkers = async () => {
      setIsGeocoding(true);
      const bounds = L.latLngBounds([]);
      let markersAdded = 0;
      let needsGeocoding = [];

      // First, add markers for clients that already have coordinates
      for (const client of clients) {
        if (client.latitude && client.longitude) {
          const lat = parseFloat(client.latitude);
          const lon = parseFloat(client.longitude);
          
          if (!isNaN(lat) && !isNaN(lon)) {
            const icon = L.divIcon({
              className: 'custom-marker',
              html: `<div style="background-color: #2563eb; color: white; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; box-shadow: 0 4px 6px rgba(0,0,0,0.3); border: 2px solid white;">${markersAdded + 1}</div>`,
              iconSize: [32, 32],
              iconAnchor: [16, 32],
              popupAnchor: [0, -32]
            });

            const popupContent = `
              <div style="padding: 8px; min-width: 200px;">
                <h3 style="font-weight: 600; font-size: 14px; margin-bottom: 4px;">${client.companyName}</h3>
                <p style="font-size: 12px; color: #4b5563; margin-bottom: 2px;">${client.contactFirstName} ${client.contactLastName}</p>
                <p style="font-size: 12px; color: #6b7280; margin-bottom: 2px;">${client.city}, ${client.state}</p>
                <p style="font-size: 11px; color: #9ca3af; margin-bottom: 8px;">${client.address}</p>
                ${onClientClick ? `<button onclick="window.dispatchEvent(new CustomEvent('mapClientClick', {detail: '${client.id}'}))" style="margin-top: 8px; padding: 4px 8px; background-color: #2563eb; color: white; font-size: 12px; border-radius: 4px; border: none; cursor: pointer;">View Details</button>` : ''}
              </div>
            `;

            const marker = L.marker([lat, lon], { icon })
              .addTo(mapInstanceRef.current)
              .bindPopup(popupContent);

            markersRef.current.push(marker);
            bounds.extend([lat, lon]);
            markersAdded++;
          }
        } else {
          // Client needs geocoding
          needsGeocoding.push(client);
        }
      }

      // Fit bounds for clients with existing coordinates
      if (markersAdded > 0) {
        mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      }

      // Now geocode clients that don't have coordinates. The backend geocodes
      // with multi-provider fallbacks, persists the result, and handles rate
      // limiting, so no client-side delay is needed.
      for (const client of needsGeocoding) {
        try {
          let result;
          if (geocodeResultCache.has(client.id)) {
            result = geocodeResultCache.get(client.id);
          } else {
            const response = await clientsAPI.geocodeClient(client.id);
            result = response.data?.result || null;
            geocodeResultCache.set(client.id, result);
          }

          if (result) {
            const lat = result.latitude;
            const lon = result.longitude;

            // Create custom icon with client number
            const icon = L.divIcon({
              className: 'custom-marker',
              html: `<div style="background-color: #2563eb; color: white; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; box-shadow: 0 4px 6px rgba(0,0,0,0.3); border: 2px solid white;">${markersAdded + 1}</div>`,
              iconSize: [32, 32],
              iconAnchor: [16, 32],
              popupAnchor: [0, -32]
            });

            const popupContent = `
              <div style="padding: 8px; min-width: 200px;">
                <h3 style="font-weight: 600; font-size: 14px; margin-bottom: 4px;">${client.companyName}</h3>
                <p style="font-size: 12px; color: #4b5563; margin-bottom: 2px;">${client.contactFirstName} ${client.contactLastName}</p>
                <p style="font-size: 12px; color: #6b7280; margin-bottom: 2px;">${client.city}, ${client.state}</p>
                <p style="font-size: 11px; color: #9ca3af; margin-bottom: 8px;">${client.address}</p>
                ${onClientClick ? `<button onclick="window.dispatchEvent(new CustomEvent('mapClientClick', {detail: '${client.id}'}))" style="margin-top: 8px; padding: 4px 8px; background-color: #2563eb; color: white; font-size: 12px; border-radius: 4px; border: none; cursor: pointer;">View Details</button>` : ''}
              </div>
            `;

            const marker = L.marker([lat, lon], { icon })
              .addTo(mapInstanceRef.current)
              .bindPopup(popupContent);

            markersRef.current.push(marker);
            bounds.extend([lat, lon]);
            markersAdded++;

            // Fit bounds periodically as markers are added
            if (markersAdded === 1 || markersAdded % 3 === 0) {
              mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
            }
          }
        } catch (error) {
          console.error(`Error geocoding customer ${client.id}:`, error);
        }
      }

      // Final fit to bounds
      if (markersAdded > 0) {
        mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      } else if (clients.length > 0) {
        // If no markers were added, show a message
        console.warn('No customers could be geocoded. Check addresses.');
      }
      
      setIsGeocoding(false);
    };

    // Listen for map client click events from popup buttons
    const handleMapClientClick = (event) => {
      const clientId = event.detail;
      const client = clients.find(c => c.id === clientId);
      if (client && onClientClick) {
        onClientClick(client);
      }
    };

    window.addEventListener('mapClientClick', handleMapClientClick);

    geocodeAndAddMarkers();

    return () => {
      window.removeEventListener('mapClientClick', handleMapClientClick);
    };
  }, [clients, onClientClick, onDragStart]);

  return (
    <div className="relative">
      <div ref={mapRef} className="w-full h-[600px] rounded-lg border border-gray-300" />
      {isGeocoding && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white px-4 py-2 rounded-lg shadow-lg border border-gray-200 z-[1000]">
          <p className="text-sm text-gray-700 flex items-center space-x-2">
            <span className="animate-spin">⏳</span>
            <span>Geocoding addresses...</span>
          </p>
        </div>
      )}
      {clients.length === 0 && !isGeocoding && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 bg-opacity-75 rounded-lg z-[999]">
          <p className="text-gray-500">No clients to display on map</p>
        </div>
      )}
    </div>
  );
};

export default ClientsMap;
